import { FunctionComponent, useMemo, useState } from 'react';
import {
    getInvoice,
    useGetOrdersQuery,
    usePostReOrderKeycardsInternalMutation,
} from '@/api/v2/order';
import { Box, Stack, SvgIcon } from '@mui/material';
import { Customer } from '@/models/customer';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@starjack/shared-components';
import { Order } from '@/models/order';
import { formatStarjackDate } from '@/utils/time';
import { getMoneyAsString } from '@/utils/money';
import IconButton from '@mui/material/IconButton';
import ArrowDownOnSquareIcon from '@heroicons/react/24/outline/ArrowDownOnSquareIcon';
import { downloadContentGeneric } from '@/utils';
import { useNotification } from '@starjack/features/notifications';
import CircularProgress from '@mui/material/CircularProgress';
import DocumentMagnifyingGlassIcon from '@heroicons/react/24/solid/DocumentMagnifyingGlassIcon';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { KeycardStatus } from '@/models/keycard';
import { SearchBoxCard } from '@/pageContents/customers/SearchBoxCard';
import { getTime } from 'date-fns';

type CustomerOrdersListProps = {
    customer: Customer;
};

export const CustomerOrdersList: FunctionComponent<CustomerOrdersListProps> = ({
    customer,
}) => {
    const { data: orders = [], isLoading: ordersLoading } = useGetOrdersQuery({
        customerId: customer.id,
    });

    const sortedOrders = useMemo(
        () =>
            [...orders].sort(
                (a, b) => getTime(b.creationDate) - getTime(a.creationDate)
            ),
        [orders]
    );

    const {
        currentItems,
        countTotal,
        page,
        setNextPage,
        itemsPerPage,
        setItemsPerPage,
    } = usePagination({
        items: sortedOrders,
        itemsPerPage: 10,
    });

    const { addNotification } = useNotification();
    const [downloadInvoiceLoading, setDownloadInvoiceLoading] =
        useState<string>('');
    const navigate = useNavigate();
    const [postReOrder, { isLoading: postReOrderLoading }] =
        usePostReOrderKeycardsInternalMutation();
    const [searchTerm, setSearchTerm] = useState<string>('');

    const onReOrder = (order: Order) => {
        postReOrder({ orderId: order.id })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Keycards wurden neu bestellt',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim neu bestellen. Bitte versuchen Sie es später noch einmal',
                });
            });
    };

    const onDownload = async (order: Order) => {
        setDownloadInvoiceLoading(order.id);
        const [downloadData, errorCode] = await getInvoice(order.id);

        if (errorCode === null) {
            downloadContentGeneric(
                `Rechnung von ${formatStarjackDate(order.creationDate)}.pdf`,
                downloadData
            );
        } else {
            if (errorCode === 404) {
                addNotification({
                    type: 'error',
                    message: 'Rechnung nicht gefunden',
                });
            } else if (errorCode >= 500) {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Herunterladen der Rechnung. Bitte versuchen Sie es später noch einmal',
                });
            }
        }

        setDownloadInvoiceLoading('');
    };

    return (
        <Stack spacing={3}>
            <SearchBoxCard
                onChange={(next) => setSearchTerm(next)}
                inputLabel='Bestellungen durchsuchen (mehrere Felder auf einmal möglich)'
            />

            <StarjackTable<Order>
                searchTerm={searchTerm}
                items={currentItems}
                count={countTotal}
                rowsPerPage={itemsPerPage}
                onRowsPerPageChange={setItemsPerPage}
                onPageChange={setNextPage}
                currentPage={page}
                itemId={'id'}
                loading={ordersLoading}
                columns={[
                    {
                        label: 'Details',
                        itemTransform: (order) => (
                            <IconButton
                                onClick={() => {
                                    navigate(
                                        `/kunden/${customer.id}/bestellungen/${order.id}`
                                    );
                                }}
                            >
                                <SvgIcon fontSize='medium'>
                                    <DocumentMagnifyingGlassIcon />
                                </SvgIcon>
                            </IconButton>
                        ),
                    },
                    {
                        label: 'Rechnung',
                        itemTransform: (order) => (
                            <IconButton
                                onClick={() => {
                                    if (order.id !== downloadInvoiceLoading) {
                                        onDownload(order);
                                    }
                                }}
                            >
                                {downloadInvoiceLoading === order.id ? (
                                    <CircularProgress size={22} />
                                ) : (
                                    <SvgIcon fontSize='medium'>
                                        <ArrowDownOnSquareIcon />
                                    </SvgIcon>
                                )}
                            </IconButton>
                        ),
                    },
                    {
                        label: 'Bestell Id',
                        itemKey: 'id',
                    },
                    {
                        label: 'Bestell Anzeige ID',
                        itemKey: 'displayId',
                    },

                    {
                        label: 'Erstellt am',
                        itemTransform: (order) =>
                            formatStarjackDate(order.creationDate),
                    },
                    {
                        label: 'Status',
                        itemKey: 'status',
                    },
                    {
                        label: 'Gekauft via',
                        itemTransform: (order) => {
                            return order.integrationRef ? 'Shop' : 'Portal';
                        },
                    },
                    {
                        label: 'Bezahlt',
                        itemTransform: (order) =>
                            getMoneyAsString(order.payAmount),
                    },

                    {
                        label: 'Frei',
                        itemTransform: (order) => (order.free ? 'Ja' : 'Nein'),
                    },

                    {
                        label: 'Vorname',
                        itemTransform: (order) => order.address.firstname,
                    },

                    {
                        label: 'Nachname',
                        itemTransform: (order) => order.address.lastname,
                    },
                    {
                        label: 'E-Mail',
                        itemTransform: (order) => order.address.company,
                    },

                    {
                        label: 'Straße',
                        itemTransform: (order) => order.address.street,
                    },

                    {
                        label: 'PLZ',
                        itemTransform: (order) => order.address.zipcode,
                    },

                    {
                        label: 'Ort',
                        itemTransform: (order) => order.address.city,
                    },

                    {
                        label: 'Land',
                        itemTransform: (order) => order.address.country,
                    },

                    {
                        label: 'Produktionsart',
                        itemTransform: (order) =>
                            order.tickets[0]?.identificationType || '-',
                    },

                    {
                        label: 'Interne Keycards Neu Bestellen',
                        itemTransform: (order) => (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <LoadingButton
                                    onClick={() => onReOrder(order)}
                                    loading={postReOrderLoading}
                                    disabled={
                                        !order.keycards.some(
                                            (k) =>
                                                k.status ===
                                                KeycardStatus.PRODUCED
                                        )
                                    }
                                    variant='contained'
                                >
                                    Bestellen
                                </LoadingButton>
                            </Box>
                        ),
                    },
                ]}
            />
        </Stack>
    );
};
